import { QdsButtonBase } from '../shared/button-base.js';
import { upgradeProperty } from '../shared/form-control.js';
import { primitiveEvent } from '../shared/primitive-utils.js';
import '../menu/qds-menu.js';
import type { QdsMenu, MenuEntry } from '../menu/qds-menu.js';

/**
 * A primary native/form action plus a separate accessible menu button.
 * @element qds-split-button
 * @attr {string} label - Visible fallback primary action label.
 * @attr {string} aria-label - Optional explicit primary action name.
 * @attr {string} group-label - Accessible action-group name; defaults to the primary label.
 * @attr {string} menu-label - Menu-button name; default More actions.
 * @attr {'primary'|'secondary'|'quiet'|'destructive'} variant - Shared button treatment.
 * @attr {'sm'|'md'|'lg'} size - Shared size; targets remain at least 44px.
 * @attr {boolean} disabled - Disable both actions, including native disabled fieldsets.
 * @attr {boolean} loading - Busy primary indicator; disable both actions and close the menu.
 * @attr {'button'|'submit'|'reset'} type - Primary form behavior; default button.
 * @attr {string} name - Primary native submitter name.
 * @attr {string} value - Primary native submitter value.
 * @attr {string} form - Native form owner ID.
 * @attr {string} formaction - Primary form action override.
 * @attr {string} formenctype - Primary form encoding override.
 * @attr {string} formmethod - Primary form method override.
 * @attr {boolean} formnovalidate - Primary skips native validation.
 * @attr {string} formtarget - Primary form target override.
 * @property {ReadonlyArray<{id: string; label: string; disabled?: boolean; type?: 'item'} | {type: 'separator'} | {type: 'group'; label: string; items: ReadonlyArray<{id: string; label: string; disabled?: boolean}>}>} items - Secondary menu actions.
 * @slot - Visible primary action content.
 * @slot icon - Decorative primary icon.
 * @slot suffix - Decorative primary suffix.
 * @csspart button - Primary native button.
 * @csspart menu-button - Secondary native menu button.
 * @csspart group - Named action group.
 * @fires click - Cancelable primary activation; menu interactions do not emit it.
 * @fires {{component: HTMLElement}} qds-action - Cancelable primary action before native submit/reset.
 * @fires {{component: HTMLElement; id: string; item: {id: string; label: string; disabled?: boolean}}} qds-select - Cancelable secondary action; preventDefault keeps the menu open.
 */
export class QdsSplitButton extends QdsButtonBase {
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'group-label', 'menu-label']; }
  private _menu = document.createElement('qds-menu') as QdsMenu;
  private _arrow?: HTMLButtonElement;
  private _group?: HTMLDivElement;
  get items(): MenuEntry[] { return this._menu.items; }
  set items(value: readonly MenuEntry[]) { this._menu.items = value; this._updateIcon(); }
  get groupLabel(): string { return this.getAttribute('group-label')?.trim() || this.label || this.textContent?.trim() || 'Split actions'; }
  set groupLabel(value: string) { this.setAttribute('group-label', value ?? ''); }
  get menuLabel(): string { return this.getAttribute('menu-label')?.trim() || 'More actions'; }
  set menuLabel(value: string) { this.setAttribute('menu-label', value ?? ''); }
  override connectedCallback(): void {
    for (const name of ['items', 'groupLabel', 'menuLabel']) upgradeProperty(this, name);
    super.connectedCallback();
    if (!this._arrow) {
      this._group = document.createElement('div'); this._group.setAttribute('part', 'group'); this._group.setAttribute('role', 'group');
      this._arrow = document.createElement('button'); this._arrow.type = 'button'; this._arrow.setAttribute('part', 'menu-button'); this._arrow.textContent = '▾';
      this._menu.anchor = this._arrow; this._menu.setAttribute('exportparts', 'menu,item,separator');
      const style = document.createElement('style'); style.textContent = `
        :host { display: inline-block; max-width: 100%; }
        [part=group] { display: inline-flex; align-items: stretch; max-width: 100%; gap: 1px; }
        [part=button] { border-start-end-radius: 0; border-end-end-radius: 0; }
        [part=menu-button] { min-width: 44px; width: 44px; padding: 0; border-start-start-radius: 0; border-end-start-radius: 0; }
      `;
      this._group.append(this._button!, this._arrow, this._menu); this.shadowRoot!.append(style, this._group);
    }
    this._arrow.addEventListener('click', this._stopAuxiliaryClick); this._menu.addEventListener('click', this._stopAuxiliaryClick);
    this._menu.addEventListener('qds-select', this._select); this._updateIcon();
  }
  override disconnectedCallback(): void {
    super.disconnectedCallback(); this._arrow?.removeEventListener('click', this._stopAuxiliaryClick);
    this._menu.removeEventListener('click', this._stopAuxiliaryClick); this._menu.removeEventListener('qds-select', this._select);
  }
  protected override _activate(): boolean { return primitiveEvent(this, 'qds-action', { component: this }, true); }
  protected override _updateIcon(): void {
    if (!this._arrow) return;
    const available = this.items.some(entry => entry.type === 'group' ? entry.items.length > 0 : entry.type !== 'separator');
    this._arrow.disabled = this.disabled || this.loading || !available;
    this._arrow.dataset.variant = this.variant; this._arrow.dataset.size = this.size; this._arrow.setAttribute('aria-label', this.menuLabel);
    this._group!.setAttribute('aria-label', this.groupLabel); this._menu.label = this.menuLabel; this._menu.disabled = this.disabled || this.loading;
  }
  showMenu(): boolean { return this._menu.show(); }
  hideMenu(): boolean { return this._menu.hide(); }
  private _stopAuxiliaryClick = (event: Event): void => { event.stopPropagation(); };
  private _select = (event: Event): void => {
    event.stopPropagation(); const detail = (event as CustomEvent<{id: string; item: {id: string; label: string; disabled?: boolean}}>).detail;
    if (!primitiveEvent(this, 'qds-select', { component: this, id: detail.id, item: { ...detail.item } }, true)) event.preventDefault();
  };
}
if (!customElements.get('qds-split-button')) customElements.define('qds-split-button', QdsSplitButton);
